import { HttpMethod } from 'constants/'
import { useMyAccount } from 'hooks/useMyAccount'
import { useUser } from 'hooks/useUser'
import { useCallback } from 'react'
import { MetaCamelCaseWithOffsetAndLimit, Opportunity, PartnerPortalDeliveryModel, SortableFields } from 'types'
import { identity, pick } from 'utils'
import { useRequest } from '../useRequest'
import { useServiceCountryUrl } from '../useServiceCountryUrl'
import { useFeatureFlags } from '@audibene/ta.react-feature-flags'
import { JourneyActivity, JourneyStatus } from '@audibene/ta.utils-opportunity'
import { useDeliveryModel } from 'hooks'

enum ApiPathname {
  OPPORTUNITIES_PATHNAME = '/opportunities/v1/opportunities',
  OPPORTUNITY_PATHNAME = '/opportunities/v1/opportunities/:opportunityId',
  JOURNEY_ACTIVITIES_PATHNAME = '/opportunities/v1/journey-activities',
}

type OpportunitySortableFields = SortableFields | 'trialEndDate' | 'isFocused'

type GetOpportunityServiceQuery = Partial<{
  customerSalesforceId?: string[]
  fitterSalesforceId?: string[]
  partnerShopSalesforceId?: string
  opportunityGid?: string[]
  deliveryModel?: PartnerPortalDeliveryModel[]
  pageSize: number
  offset: number
  sortBy: OpportunitySortableFields
  sort: string
  status?: JourneyStatus[]
  isCurrent?: boolean
  strict: boolean
}>

type GetJourneyActivitiesQuery = Partial<{
  opportunityId: string[]
  offset: number
  sortBy: string
  sort: string
}>

type UpdateOpportunityFocusReasonsQuery = {
  opportunityId: string
  hadFocusReasonsAddressed: boolean
}

type UpdateOpportunityQuery = {
  opportunityId: string
  fitterGid?: string
  fitterSalesforceId?: string
}

type GetOpportunityServiceResponse = { data: Opportunity[]; meta: MetaCamelCaseWithOffsetAndLimit }
type GetJourneyActivitiesResponse = { data: JourneyActivity[]; meta: MetaCamelCaseWithOffsetAndLimit }

interface UseHookContext {
  getCurrentOpportunity: (query: GetOpportunityServiceQuery) => Promise<GetOpportunityServiceResponse>
  getLatestOpportunities: (query: GetOpportunityServiceQuery) => Promise<GetOpportunityServiceResponse>
  getJourneyActivities: (query: GetJourneyActivitiesQuery) => Promise<GetJourneyActivitiesResponse>
  updateOpportunity: (query: UpdateOpportunityQuery) => Promise<Opportunity>
  updateFocusReasonResolved: (query: UpdateOpportunityFocusReasonsQuery) => Promise<Opportunity>
}

const filterQueryToSearchParams = identity // Take all that is passed in and apply to query params

type Options = {
  restrictQueryToOpportunityFitter?: boolean
  skipFitterScope?: boolean
}

const useOpportunityService = (options?: Options): UseHookContext => {
  const { url: baseUrl } = useServiceCountryUrl('opportunity')
  const [isNeoEnabled] = useFeatureFlags(['NEO'])
  const { roleFlags } = useUser()
  const { data: account } = useMyAccount()
  const { deliveryModel: selectedDeliveryModel } = useDeliveryModel()

  const restrictQueryToOpportunityFitter = Boolean(
    roleFlags?.isPartnerFitter || options?.restrictQueryToOpportunityFitter
  )

  const getOpportunitiesRequest = useRequest<GetOpportunityServiceQuery, GetOpportunityServiceResponse>({
    baseUrl,
    filterQueryToSearchParams,
    pathname: ApiPathname.OPPORTUNITIES_PATHNAME,
  })

  const getLatestOpportunities = useCallback(
    (query: GetOpportunityServiceQuery) => {
      const fitterSalesforceId = options?.skipFitterScope
        ? undefined
        : restrictQueryToOpportunityFitter && account?.gid
        ? [account.gid]
        : query.fitterSalesforceId

      const deliveryModels = query.deliveryModel
        ? query.deliveryModel
        : selectedDeliveryModel
        ? [selectedDeliveryModel]
        : undefined

      return getOpportunitiesRequest({
        sort: 'desc',
        sortBy: 'createdAt',
        ...query,
        deliveryModel: isNeoEnabled ? undefined : deliveryModels,
        fitterSalesforceId,
      })
    },
    [account, selectedDeliveryModel, getOpportunitiesRequest, isNeoEnabled, options, restrictQueryToOpportunityFitter]
  )

  const getCurrentOpportunity = useCallback(
    (query: GetOpportunityServiceQuery) => {
      return getOpportunitiesRequest({
        ...query,
        isCurrent: true,
      })
    },
    [getOpportunitiesRequest]
  )

  const getOpportunityJourneyActivitiesRequest = useRequest<GetJourneyActivitiesQuery, GetJourneyActivitiesResponse>({
    baseUrl,
    filterQueryToSearchParams,
    pathname: ApiPathname.JOURNEY_ACTIVITIES_PATHNAME,
  })

  const getJourneyActivities = useCallback(
    (query: GetJourneyActivitiesQuery) =>
      getOpportunityJourneyActivitiesRequest({
        sort: 'desc',
        sortBy: 'opportunityId',
        ...query,
      }),
    [getOpportunityJourneyActivitiesRequest]
  )

  const updateFocusReasonResolved = useRequest<UpdateOpportunityFocusReasonsQuery, Opportunity>({
    baseUrl,
    filterQueryToRequestBody: pick(['hadFocusReasonsAddressed']),
    filterQueryToRouteParams: pick(['opportunityId']),
    method: HttpMethod.PATCH,
    pathname: ApiPathname.OPPORTUNITY_PATHNAME,
  })

  const updateOpportunity = useRequest<UpdateOpportunityQuery, Opportunity>({
    baseUrl,
    filterQueryToRequestBody: pick(['fitterGid', 'fitterSalesforceId']),
    filterQueryToRouteParams: pick(['opportunityId']),
    method: HttpMethod.PATCH,
    pathname: ApiPathname.OPPORTUNITY_PATHNAME,
  })

  return {
    getCurrentOpportunity,
    getJourneyActivities,
    getLatestOpportunities,
    updateFocusReasonResolved,
    updateOpportunity,
  }
}

export { useOpportunityService }
export type { OpportunitySortableFields, UpdateOpportunityQuery }
